import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/">
            <Button variant="outline" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>

          <div className="bg-white rounded-xl shadow-md p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Terms & Conditions
            </h1>
            <p className="text-gray-600 mb-8">
              <strong>Effective Date:</strong> December 2025<br />
              <strong>Last Updated:</strong> December 2025
            </p>

            <div className="space-y-6 text-gray-700">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Agreement to Terms</h2>
                <p>
                  Welcome to Livington Designs. These Terms and Conditions ("Terms") govern your use of our website and painting services. By accessing our website, requesting an estimate, or engaging our services, you agree to be bound by these Terms.
                </p>
                <p className="mt-3">
                  If you do not agree with any part of these Terms, please do not use our website or services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Services Description</h2>
                <p>
                  Livington Designs provides residential painting services in Los Angeles and surrounding areas, including but not limited to:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Interior painting</li>
                  <li>Exterior painting</li>
                  <li>Cabinet painting and refinishing</li>
                  <li>Trim and detail work</li>
                  <li>Wall preparation and repairs</li>
                  <li>Color consultation</li>
                </ul>
                <p className="mt-3">
                  Specific services and scope will be detailed in individual project estimates and contracts.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Estimate Process</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">3.1 Free Estimates</h3>
                <p>
                  We provide free, no-obligation written estimates for painting projects. Estimates are based on:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>In-home consultation and assessment</li>
                  <li>Project scope and requirements</li>
                  <li>Surface conditions and preparation needs</li>
                  <li>Materials and labor required</li>
                  <li>Timeline and scheduling considerations</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">3.2 Estimate Validity</h3>
                <p>
                  Written estimates are valid for 30 days from the date of issue. After this period, pricing and availability may be subject to change based on market conditions and scheduling.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">3.3 Estimate Accuracy</h3>
                <p>
                  While we strive to provide accurate estimates, final pricing may vary if:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Hidden damage or issues are discovered during preparation</li>
                  <li>Project scope changes at customer request</li>
                  <li>Additional services are requested</li>
                  <li>Unforeseen circumstances arise</li>
                </ul>
                <p className="mt-2">
                  Any changes to the original estimate will be communicated and approved by the customer before proceeding.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Contract and Payment Terms</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.1 Written Contract</h3>
                <p>
                  Once you accept an estimate, a written contract will be provided detailing:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Complete scope of work</li>
                  <li>Materials to be used</li>
                  <li>Total project cost</li>
                  <li>Payment schedule</li>
                  <li>Project timeline</li>
                  <li>Terms and conditions</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.2 Payment Schedule</h3>
                <p>
                  Payment terms vary by project size and typically follow this structure:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Deposit:</strong> 30-50% upon contract signing</li>
                  <li><strong>Progress Payment:</strong> 25-40% at project midpoint (for larger projects)</li>
                  <li><strong>Final Payment:</strong> Remaining balance upon satisfactory completion</li>
                </ul>
                <p className="mt-2">
                  Specific payment terms will be outlined in your individual contract.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.3 Accepted Payment Methods</h3>
                <p>We accept the following payment methods:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Check</li>
                  <li>Cash</li>
                  <li>Credit/Debit cards (processing fees may apply)</li>
                  <li>Bank transfer</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.4 Late Payments</h3>
                <p>
                  Payments not received within 10 days of the due date may be subject to a late fee of 1.5% per month or the maximum rate allowed by law, whichever is less. Work may be suspended until payment is received.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Customer Responsibilities</h2>
                <p>To ensure successful project completion, customers agree to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li>Provide accurate information about the property and project requirements</li>
                  <li>Remove or cover furniture, decorations, and personal items from work areas</li>
                  <li>Ensure safe access to all areas requiring painting</li>
                  <li>Provide access to electrical outlets and water (if needed)</li>
                  <li>Secure pets during work hours</li>
                  <li>Notify us of any hazardous materials (e.g., lead paint, asbestos)</li>
                  <li>Obtain necessary permits or HOA approvals (if applicable)</li>
                  <li>Inform us of any known structural or moisture issues</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Our Responsibilities</h2>
                <p>Livington Designs commits to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li>Provide professional, quality painting services</li>
                  <li>Use quality materials as specified in the contract</li>
                  <li>Complete work in a timely manner according to the agreed schedule</li>
                  <li>Maintain a clean and safe work environment</li>
                  <li>Protect surfaces not being painted with appropriate coverings</li>
                  <li>Perform daily cleanup and final thorough cleanup upon completion</li>
                  <li>Follow industry best practices and safety standards</li>
                  <li>Communicate regularly about project progress</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Project Timeline and Scheduling</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">7.1 Start Date</h3>
                <p>
                  Project start dates are scheduled upon contract signing and deposit payment. We will make every effort to begin work on the scheduled date, but delays may occur due to:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Weather conditions (for exterior work)</li>
                  <li>Material availability</li>
                  <li>Unforeseen circumstances</li>
                  <li>Customer-requested delays</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">7.2 Project Duration</h3>
                <p>
                  Estimated project duration will be provided in the contract. Actual completion time may vary based on weather, scope changes, or unforeseen issues. We will communicate any significant delays promptly.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">7.3 Work Hours</h3>
                <p>
                  Standard work hours are Monday through Friday, 8:00 AM to 6:00 PM, and Saturday 9:00 AM to 4:00 PM. Alternative arrangements can be made with advance notice.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Warranty and Guarantee</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">8.1 Workmanship Warranty</h3>
                <p>
                  We guarantee our workmanship for a period of one (1) year from project completion. This warranty covers:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Peeling or blistering due to improper application</li>
                  <li>Excessive fading (interior only)</li>
                  <li>Poor adhesion due to inadequate surface preparation</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">8.2 Warranty Exclusions</h3>
                <p>The warranty does not cover:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Normal wear and tear</li>
                  <li>Damage from accidents, abuse, or neglect</li>
                  <li>Damage from improper maintenance or cleaning</li>
                  <li>Issues arising from structural defects or moisture problems</li>
                  <li>Color fading due to sun exposure (exterior)</li>
                  <li>Damage from subsequent renovations or modifications</li>
                  <li>Issues caused by settling, cracking, or movement of the structure</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">8.3 Warranty Claims</h3>
                <p>
                  To make a warranty claim, contact us with photos and description of the issue. We will assess the situation and, if covered, repair at no charge within a reasonable timeframe.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Limitation of Liability</h2>
                <p>
                  To the fullest extent permitted by law, Livington Designs' total liability for any claims arising from our services shall not exceed the total amount paid for the specific project. We are not liable for:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Indirect, incidental, or consequential damages</li>
                  <li>Pre-existing conditions not disclosed or discoverable during inspection</li>
                  <li>Damage to undisclosed items left in work areas</li>
                  <li>Normal settling cracks or structural movement</li>
                  <li>Color variations due to lighting, sheen, or application conditions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Change Orders</h2>
                <p>
                  Any changes to the original scope of work must be requested in writing and will require a written change order specifying:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Description of additional or changed work</li>
                  <li>Additional cost (or credit, if applicable)</li>
                  <li>Impact on project timeline</li>
                  <li>Both parties' signatures</li>
                </ul>
                <p className="mt-2">
                  No additional work will be performed without an approved change order.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Insurance and Licensing</h2>
                <p>
                  Livington Designs maintains appropriate insurance coverage for our business operations. Proof of insurance is available upon request. We comply with all applicable local, state, and federal regulations.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Dispute Resolution</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">12.1 Good Faith Resolution</h3>
                <p>
                  In the event of a dispute, both parties agree to first attempt to resolve the matter through good faith negotiation and direct communication.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">12.2 Mediation</h3>
                <p>
                  If direct negotiation fails, the parties agree to participate in mediation before pursuing legal action. Mediation costs will be shared equally.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">12.3 Governing Law</h3>
                <p>
                  These Terms shall be governed by the laws of the State of California. Any legal proceedings shall be conducted in Los Angeles County, California.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Website Use</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">13.1 Intellectual Property</h3>
                <p>
                  All content on our website, including text, images, logos, and graphics, is the property of Livington Designs and protected by copyright and trademark laws.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">13.2 Acceptable Use</h3>
                <p>You agree not to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Use our website for any unlawful purpose</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Transmit viruses or malicious code</li>
                  <li>Copy, reproduce, or distribute our content without permission</li>
                  <li>Interfere with the proper functioning of the website</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">14. Modifications to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">15. Severability</h2>
                <p>
                  If any provision of these Terms is found to be unenforceable or invalid, the remaining provisions shall continue in full force and effect.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">16. Entire Agreement</h2>
                <p>
                  These Terms, together with any written contract for specific services, constitute the entire agreement between you and Livington Designs regarding our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">17. Contact Information</h2>
                <p>
                  For questions about these Terms or our services, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg mt-4">
                  <p><strong>Livington Designs</strong></p>
                  <p className="mt-2">4512 Sunset Boulevard<br />Los Angeles, CA 90027</p>
                  <p className="mt-2">Phone: <a href="tel:+18717871113" className="text-blue-900 hover:underline">+1-871-7871-113</a></p>
                  <p>Email: <a href="mailto:info@livingtone.store" className="text-blue-900 hover:underline">info@livingtone.store</a></p>
                </div>
              </section>

              <section className="border-t border-gray-200 pt-6 mt-8">
                <p className="text-sm text-gray-600">
                  By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Livington Designs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;
